import React, { useMemo } from 'react';

import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';

import SurchargeAmountCell from './SurchargeAmountCell';
import { computeSurchargeMargin } from './computeSurchargeMargin';
import { SEASON_GRIDS, WEEKDAYS, WEEKDAY_LABELS, isSeasonApplied } from './surchargeGridsModel';
import type { SeasonGridKey, SurchargeMargin, SurchargeSessionState, Weekday } from './types';

const BLANK = '-';

interface Props {
  currencyCode: string;
  fxRateAudToNative: number | null;
  onCostChange: (seasonKey: SeasonGridKey, weekday: Weekday, value: number | null) => void;
  onSellChange: (seasonKey: SeasonGridKey, weekday: Weekday, value: number | null) => void;
  session: SurchargeSessionState;
}

function formatSellLocal(margin: SurchargeMargin) {
  return margin.sellLocal == null ? BLANK : String(margin.sellLocal);
}

function formatCostAud(margin: SurchargeMargin) {
  return margin.costAud == null ? BLANK : margin.costAud.toFixed(2);
}

function formatMarginPct(margin: SurchargeMargin) {
  return margin.marginFraction == null ? BLANK : `${(margin.marginFraction * 100).toFixed(1)}%`;
}

function isLosingMoney(margin: SurchargeMargin) {
  return margin.marginFraction != null && margin.marginFraction < 0;
}

function impliedCostPlaceholder(margin: SurchargeMargin) {
  return margin.costLocalImplied == null ? undefined : String(margin.costLocalImplied);
}

function rowLabels(currencyCode: string) {
  return {
    costAud: 'Cost AUD (effective)',
    costLocal: `Cost ${currencyCode} (typed)`,
    sellLocal: `Sell ${currencyCode} (rounded)`,
  };
}

// The label column stays put while the 21 weekday columns scroll, so a figure is never orphaned
// from the row it belongs to. Sticky needs separate borders: collapsed ones scroll away with the
// cell instead of sticking to it.
const stickyLabelSx = {
  backgroundColor: 'background.paper',
  borderBottom: '1px solid',
  borderColor: 'divider',
  left: 0,
  minWidth: 150,
  position: 'sticky',
  whiteSpace: 'nowrap',
  zIndex: 2,
} as const;

const stickyHeaderLabelSx = { ...stickyLabelSx, fontWeight: 700, zIndex: 3 } as const;

const cellSx = { borderBottom: '1px solid', borderColor: 'divider', px: 0.5 } as const;

function groupSplitSx(isFirstOfGroup: boolean) {
  return isFirstOfGroup ? { borderLeft: '2px solid', borderLeftColor: 'text.disabled' } : undefined;
}

export default function SurchargeDowTable({
  currencyCode,
  fxRateAudToNative,
  onCostChange,
  onSellChange,
  session,
}: Props) {
  const labels = useMemo(() => rowLabels(currencyCode), [currencyCode]);

  const marginFor = useMemo(() => {
    const bySeason = {} as Record<SeasonGridKey, Record<Weekday, SurchargeMargin>>;
    for (const season of SEASON_GRIDS) {
      const byWeekday = {} as Record<Weekday, SurchargeMargin>;
      for (const weekday of WEEKDAYS) {
        byWeekday[weekday] = computeSurchargeMargin(
          { sellAud: session.grids[season.key][weekday], costLocal: session.costs[season.key][weekday] },
          fxRateAudToNative ?? 0,
        );
      }
      bySeason[season.key] = byWeekday;
    }
    return bySeason;
  }, [fxRateAudToNative, session]);

  const computedRows = [
    { format: formatSellLocal, label: labels.sellLocal },
    { format: formatCostAud, label: labels.costAud },
  ];

  return (
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ borderCollapse: 'separate', borderSpacing: 0, width: 'auto' }}>
        <TableHead>
          <TableRow>
            <TableCell sx={stickyHeaderLabelSx} />
            {SEASON_GRIDS.map((season, seasonIndex) => (
              <TableCell
                align="center"
                colSpan={WEEKDAYS.length}
                key={season.key}
                sx={{ ...cellSx, ...groupSplitSx(seasonIndex > 0), fontWeight: 700 }}
              >
                {season.label}
                {!isSeasonApplied(season.key, session.grids) && (
                  <Typography color="warning.main" sx={{ display: 'block', fontWeight: 400 }} variant="caption">
                    No travel month marked {season.label}, nothing here is applied
                  </Typography>
                )}
              </TableCell>
            ))}
          </TableRow>

          <TableRow>
            <TableCell sx={stickyHeaderLabelSx}>DOW</TableCell>
            {SEASON_GRIDS.map((season, seasonIndex) =>
              WEEKDAYS.map((weekday, weekdayIndex) => (
                <TableCell
                  align="center"
                  key={`${season.key}-${weekday}`}
                  sx={{ ...cellSx, ...groupSplitSx(seasonIndex > 0 && weekdayIndex === 0), fontWeight: 700 }}
                >
                  {WEEKDAY_LABELS[weekday]}
                </TableCell>
              )),
            )}
          </TableRow>
        </TableHead>

        <TableBody>
          <TableRow>
            <TableCell sx={stickyLabelSx}>Sell AUD</TableCell>
            {SEASON_GRIDS.map((season, seasonIndex) =>
              WEEKDAYS.map((weekday, weekdayIndex) => (
                <TableCell
                  key={`${season.key}-${weekday}`}
                  sx={{ ...cellSx, ...groupSplitSx(seasonIndex > 0 && weekdayIndex === 0) }}
                >
                  <SurchargeAmountCell
                    kind="sell"
                    onChange={onSellChange}
                    seasonKey={season.key}
                    value={session.grids[season.key][weekday]}
                    weekday={weekday}
                  />
                </TableCell>
              )),
            )}
          </TableRow>

          {computedRows.map((row) => (
            <TableRow key={row.label}>
              <TableCell sx={stickyLabelSx}>{row.label}</TableCell>
              {SEASON_GRIDS.map((season, seasonIndex) =>
                WEEKDAYS.map((weekday, weekdayIndex) => (
                  <TableCell
                    align="center"
                    key={`${season.key}-${weekday}`}
                    sx={{ ...cellSx, ...groupSplitSx(seasonIndex > 0 && weekdayIndex === 0) }}
                  >
                    <Typography color="text.secondary" variant="body2">
                      {row.format(marginFor[season.key][weekday])}
                    </Typography>
                  </TableCell>
                )),
              )}
            </TableRow>
          ))}

          <TableRow>
            <TableCell sx={stickyLabelSx}>{labels.costLocal}</TableCell>
            {SEASON_GRIDS.map((season, seasonIndex) =>
              WEEKDAYS.map((weekday, weekdayIndex) => (
                <TableCell
                  key={`${season.key}-${weekday}`}
                  sx={{ ...cellSx, ...groupSplitSx(seasonIndex > 0 && weekdayIndex === 0) }}
                >
                  <SurchargeAmountCell
                    kind="cost"
                    onChange={onCostChange}
                    placeholder={impliedCostPlaceholder(marginFor[season.key][weekday])}
                    seasonKey={season.key}
                    value={session.costs[season.key][weekday]}
                    weekday={weekday}
                  />
                </TableCell>
              )),
            )}
          </TableRow>

          <TableRow>
            <TableCell sx={stickyLabelSx}>% Margin</TableCell>
            {SEASON_GRIDS.map((season, seasonIndex) =>
              WEEKDAYS.map((weekday, weekdayIndex) => (
                <TableCell
                  align="center"
                  key={`${season.key}-${weekday}`}
                  sx={{ ...cellSx, ...groupSplitSx(seasonIndex > 0 && weekdayIndex === 0) }}
                >
                  <Typography
                    color={isLosingMoney(marginFor[season.key][weekday]) ? 'error.main' : 'text.secondary'}
                    fontWeight={isLosingMoney(marginFor[season.key][weekday]) ? 700 : undefined}
                    variant="body2"
                  >
                    {formatMarginPct(marginFor[season.key][weekday])}
                  </Typography>
                </TableCell>
              )),
            )}
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}
